import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  Truck, 
  Shield, 
  Package,
  Loader2
} from "lucide-react";
import { useT, useLanguage } from "../context/LanguageContext";
import { shopT } from "../translations/ui";
import LanguageSwitcher from "../components/LanguageSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  logo: "/logo.png",
};

// EU countries list for shipping detection
const EU_COUNTRIES = [
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic", 
  "Denmark", "Estonia", "Finland", "France", "Germany", "Greece", "Hungary", 
  "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta", "Netherlands", 
  "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain", "Sweden"
];

export default function Shop() {
  const [product, setProduct] = useState(null);
  const [stock, setStock] = useState(null);
  const [shippingRates, setShippingRates] = useState(null);
  const [currency, setCurrency] = useState("DKK");
  const [currencies, setCurrencies] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    shipping_address: "",
    shipping_city: "",
    shipping_postal_code: "",
    shipping_country: "Denmark"
  });

  // Fetch product and stock data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, stockRes, shippingRes, currenciesRes] = await Promise.all([
          axios.get(`${API}/shop/product/the-gaffer?currency=${currency}`),
          axios.get(`${API}/shop/stock`),
          axios.get(`${API}/shop/shipping?currency=${currency}`),
          axios.get(`${API}/shop/currencies`)
        ]);
        
        setProduct(productRes.data);
        setStock(stockRes.data);
        setShippingRates(shippingRes.data);
        setCurrencies(currenciesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [currency]);

  // Determine shipping region based on country
  const getShippingRegion = (country) => {
    if (country === "Denmark") return "DK";
    if (EU_COUNTRIES.includes(country)) return "EU";
    return "INTL";
  };

  // Calculate total
  const calculateTotal = () => {
    if (!product || !shippingRates) return { subtotal: 0, shipping: 0, total: 0 };
    
    const shippingRegion = getShippingRegion(formData.shipping_country);
    const shipping = shippingRates[shippingRegion]?.price || 0;
    const subtotal = product.price;
    const total = subtotal + shipping;
    
    return { subtotal, shipping, total, shippingRegion };
  };

  // Format price
  const formatPrice = (amount) => {
    if (!currencies) return `${amount}`;
    const symbol = currencies.symbols[currency];
    
    if (currency === "DKK") {
      return `${amount.toLocaleString('da-DK')} ${symbol}`;
    }
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  // Handle checkout
  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!selectedSize) {
      alert("Please select a size");
      return;
    }
    
    setIsCheckingOut(true);
    
    try {
      const { shippingRegion } = calculateTotal();
      
      const response = await axios.post(`${API}/shop/checkout`, {
        product_id: "the-gaffer",
        size: selectedSize,
        shipping_region: shippingRegion,
        currency: currency,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        shipping_address: formData.shipping_address,
        shipping_city: formData.shipping_city,
        shipping_postal_code: formData.shipping_postal_code,
        shipping_country: formData.shipping_country,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Checkout error:", error);
      alert(error.response?.data?.detail || "Checkout failed. Please try again.");
      setIsCheckingOut(false);
    }
  };

  // Image navigation
  const nextImage = () => {
    if (product) {
      setSelectedImage((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product) {
      setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (!stock?.is_available) {
    return (
      <div className="min-h-screen bg-black">
        <header className="border-b border-white/10 p-6">
          <div className="max-w-7xl mx-auto flex items-center gap-6">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Link to="/">
              <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
            </Link>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="heading-xl text-4xl text-white mb-6">Pre-Order Sold Out</h1>
          <p className="text-white/60 mb-8">
            All {stock?.total_available} pairs from our first pre-order batch have been sold. 
            Join the waitlist to be notified when more stock becomes available.
          </p>
          <Link to="/" className="btn-primary inline-block">
            Join Waitlist
          </Link>
        </main>
      </div>
    );
  }

  const { subtotal, shipping, total } = calculateTotal();

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Link to="/">
              <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
            </Link>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Currency Selector */}
            <div className="flex items-center gap-2">
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="bg-transparent text-white border border-white/20 px-3 py-1 text-sm focus:outline-none focus:border-white/40"
              >
                {currencies?.currencies.map((curr) => (
                  <option key={curr} value={curr} className="bg-black">
                    {curr} ({currencies.symbols[curr]})
                  </option>
                ))}
              </select>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Stock Banner */}
      <div className="bg-white/5 border-b border-white/10 py-3">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-white/80 text-sm">
            <span className="text-white font-semibold">{stock?.remaining}</span> of {stock?.total_available} pairs remaining
            <span className="mx-3">•</span>
            <span className="text-white/60">Limited Pre-Order Edition</span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Product Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative aspect-square bg-[#0A0A0A] border border-white/10 overflow-hidden">
              <img
                src={product?.images[selectedImage]}
                alt={product?.name}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Thumbnails */}
            <div className="flex gap-2">
              {product?.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`w-20 h-20 border ${selectedImage === idx ? 'border-white' : 'border-white/10 hover:border-white/30'} transition-all overflow-hidden`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-8">
            <div>
              <p className="label-text text-white/40 mb-2">Pre-Order</p>
              <h1 className="heading-xl text-4xl md:text-5xl text-white mb-4">{product?.name}</h1>
              <p className="text-3xl text-white heading-md">
                {formatPrice(product?.price)}
              </p>
              <p className="text-white/40 text-sm mt-1">+ shipping</p>
            </div>

            <p className="body-text text-white/60">
              {product?.description}
            </p>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 border border-white/10">
                <Shield className="w-6 h-6 text-white mx-auto mb-2" />
                <p className="text-white/60 text-xs">TEX-Membrane</p>
              </div>
              <div className="text-center p-4 border border-white/10">
                <Package className="w-6 h-6 text-white mx-auto mb-2" />
                <p className="text-white/60 text-xs">Handcrafted</p>
              </div>
              <div className="text-center p-4 border border-white/10">
                <Truck className="w-6 h-6 text-white mx-auto mb-2" />
                <p className="text-white/60 text-xs">Sept 2026</p>
              </div>
            </div>

            {/* Size Selection */}
            <div>
              <label className="block text-white text-sm uppercase tracking-wider mb-3">
                Select Size
              </label>
              <div className="grid grid-cols-3 gap-2">
                {product?.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`py-3 border ${
                      selectedSize === size
                        ? 'border-white bg-white text-black'
                        : 'border-white/20 text-white hover:border-white/40'
                    } transition-all text-sm`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Checkout Form Toggle */}
            {!showCheckoutForm ? (
              <button
                onClick={() => setShowCheckoutForm(true)}
                disabled={!selectedSize}
                className={`btn-primary w-full py-4 ${!selectedSize ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {selectedSize ? 'Continue to Checkout' : 'Select a Size'}
              </button>
            ) : (
              <form onSubmit={handleCheckout} className="space-y-6">
                <div className="border-t border-white/10 pt-6">
                  <h3 className="heading-md text-lg text-white mb-4">Shipping Information</h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Full Name *"
                        required
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        className="input-minimal"
                      />
                      <input
                        type="email"
                        placeholder="Email *"
                        required
                        value={formData.customer_email}
                        onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                        className="input-minimal"
                      />
                    </div>
                    
                    <input
                      type="text"
                      placeholder="Address *"
                      required
                      value={formData.shipping_address}
                      onChange={(e) => setFormData({...formData, shipping_address: e.target.value})}
                      className="input-minimal"
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="City *"
                        required
                        value={formData.shipping_city}
                        onChange={(e) => setFormData({...formData, shipping_city: e.target.value})}
                        className="input-minimal"
                      />
                      <input
                        type="text"
                        placeholder="Postal Code *"
                        required
                        value={formData.shipping_postal_code}
                        onChange={(e) => setFormData({...formData, shipping_postal_code: e.target.value})}
                        className="input-minimal"
                      />
                    </div>
                    
                    <select
                      required
                      value={formData.shipping_country}
                      onChange={(e) => setFormData({...formData, shipping_country: e.target.value})}
                      className="w-full bg-transparent text-white border-b border-white/20 py-3 focus:outline-none focus:border-white"
                    >
                      <option value="Denmark" className="bg-black">Denmark</option>
                      <optgroup label="European Union" className="bg-black">
                        {EU_COUNTRIES.filter(c => c !== "Denmark").map(country => (
                          <option key={country} value={country} className="bg-black">{country}</option>
                        ))}
                      </optgroup>
                      <optgroup label="International" className="bg-black">
                        <option value="United States" className="bg-black">United States</option>
                        <option value="United Kingdom" className="bg-black">United Kingdom</option>
                        <option value="Norway" className="bg-black">Norway</option>
                        <option value="Switzerland" className="bg-black">Switzerland</option>
                        <option value="Canada" className="bg-black">Canada</option>
                        <option value="Australia" className="bg-black">Australia</option>
                        <option value="Other" className="bg-black">Other</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="border border-white/10 p-6 bg-white/[0.02]">
                  <h3 className="heading-md text-lg text-white mb-4">Order Summary</h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/60">{product?.name} ({selectedSize})</span>
                      <span className="text-white">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Shipping ({shippingRates?.[getShippingRegion(formData.shipping_country)]?.name})</span>
                      <span className="text-white">{formatPrice(shipping)}</span>
                    </div>
                    <div className="border-t border-white/10 pt-3 flex justify-between">
                      <span className="text-white font-semibold">Total</span>
                      <span className="text-white font-semibold text-lg">{formatPrice(total)}</span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isCheckingOut}
                  className="btn-primary w-full py-4 flex items-center justify-center gap-2"
                >
                  {isCheckingOut ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Complete Pre-Order
                    </>
                  )}
                </button>

                <p className="text-white/40 text-xs text-center">
                  Expected delivery: September 2026. You will be charged now.
                </p>
              </form>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 p-6 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/30 text-sm">
            © 2026 Gaffer Athletics. Engineering for the Sideline.
          </p>
        </div>
      </footer>
    </div>
  );
}
